/* ---- GeoAttest App — Entry point ----
 * Vanilla TS SPA with tab routing.
 *
 * 設計: ページ DOM は detach せず display:none/block で切替する。これにより
 * MapPage の OpenLayers Map と SDK monitoring がタブ切替で再初期化されず、
 * 直近の location / 環境推定 / ジオフェンス候補が即座に再表示される。
 */

import '../css/styles.css';
import { renderTabBar } from './components/TabBar';
import { EventDetailPage } from './pages/EventDetailPage';
import { HistoryPage } from './pages/HistoryPage';
import { renderLoginPage } from './pages/LoginPage';
import { MapPage } from './pages/MapPage';
import { SettingsPage } from './pages/SettingsPage';
import { authService } from './services/auth.service';
import type { PageId, TabId } from './types';

class App {
  private root: HTMLElement;
  private pagesContainer: HTMLElement;
  private tabBarContainer: HTMLElement;
  private currentPage: PageId = 'login';
  private activeTab: TabId = 'map';

  // Page instances (singleton, DOM is kept alive across tab switches)
  private mapPage: MapPage | null = null;
  private historyPage: HistoryPage | null = null;
  private detailPage: EventDetailPage | null = null;
  private settingsPage: SettingsPage | null = null;

  constructor() {
    this.root = document.getElementById('app')!;
    this.pagesContainer = document.createElement('div');
    this.pagesContainer.id = 'pages';
    this.pagesContainer.style.cssText =
      'flex:1; display:flex; flex-direction:column; min-height:0;';
    this.tabBarContainer = document.createElement('div');
    this.tabBarContainer.id = 'tabbar';
    this.root.append(this.pagesContainer, this.tabBarContainer);
  }

  init(): void {
    const hasToken = authService.init();
    if (hasToken) {
      this.currentPage = 'map';
      this.activeTab = 'map';
    } else {
      this.currentPage = 'login';
    }
    this.render();
  }

  private render(): void {
    if (this.currentPage === 'login') {
      this.renderLogin();
      return;
    }

    const pageEl = this.renderCurrentPage();

    // ページ DOM を pagesContainer に保持し、display で切替 (detach なし)
    if (!this.pagesContainer.contains(pageEl)) {
      this.pagesContainer.appendChild(pageEl);
    }
    Array.from(this.pagesContainer.children).forEach((child) => {
      (child as HTMLElement).style.display = child === pageEl ? '' : 'none';
    });

    // tab bar
    this.tabBarContainer.innerHTML = '';
    const effectiveTab = this.currentPage === 'detail' ? 'history' : this.activeTab;
    const tabBar = renderTabBar(effectiveTab, (tab) => this.switchTab(tab));
    this.tabBarContainer.appendChild(tabBar);
  }

  private renderLogin(): void {
    // ログイン中はページコンテナとタブを隠す (DOM は破棄せず class 切替も検討可)
    this.pagesContainer.innerHTML = '';
    this.tabBarContainer.innerHTML = '';
    const loginEl = renderLoginPage(() => {
      this.currentPage = 'map';
      this.activeTab = 'map';
      this.render();
    });
    this.pagesContainer.appendChild(loginEl);
  }

  private renderCurrentPage(): HTMLElement {
    // detail はタブ切替時に破棄 (履歴 → 詳細 → 別タブ で残らないように)
    if (this.currentPage !== 'detail' && this.detailPage) {
      this.removeFromContainer(this.detailPageEl());
      this.detailPage.destroy();
      this.detailPage = null;
    }

    switch (this.currentPage) {
      case 'map':
        if (!this.mapPage) {
          this.mapPage = new MapPage();
        }
        return this.mapPage.render();

      case 'history':
        if (!this.historyPage) {
          this.historyPage = new HistoryPage((eventId) => {
            this.showEventDetail(eventId);
          });
        }
        return this.historyPage.render();

      case 'detail':
        return this.detailPage!.render();

      case 'settings':
        if (!this.settingsPage) {
          this.settingsPage = new SettingsPage(() => {
            // logout: 全ページインスタンスと DOM を破棄
            this.mapPage?.destroy();
            this.mapPage = null;
            this.historyPage = null;
            this.detailPage?.destroy();
            this.detailPage = null;
            this.settingsPage = null;
            this.pagesContainer.innerHTML = '';
            this.currentPage = 'login';
            this.activeTab = 'map';
            this.render();
          });
        }
        return this.settingsPage.render();

      default: {
        const el = document.createElement('div');
        el.textContent = 'Unknown page';
        return el;
      }
    }
  }

  private detailPageEl(): HTMLElement | null {
    // EventDetailPage のルート要素 (内部で this.el を保持していると仮定)
    // 取得できない場合は null
    const elements = Array.from(this.pagesContainer.children);
    // detailPage が render() で返した要素を再特定するのは困難。
    // 代替として、新しい detail に切り替わるたびに container 内で
    // map/history/settings 以外を全て除去するアプローチを採用。
    return elements.find((el) => {
      const e = el as HTMLElement;
      return (
        e !== (this.mapPage as { el?: HTMLElement } | null)?.el &&
        e !== (this.historyPage as { el?: HTMLElement } | null)?.el &&
        e !== (this.settingsPage as { el?: HTMLElement } | null)?.el
      );
    }) as HTMLElement | null;
  }

  private removeFromContainer(el: HTMLElement | null): void {
    if (el && this.pagesContainer.contains(el)) {
      this.pagesContainer.removeChild(el);
    }
  }

  private switchTab(tab: TabId): void {
    if (this.activeTab === tab && this.currentPage === tab) return;
    this.activeTab = tab;
    this.currentPage = tab;
    this.render();
  }

  private showEventDetail(eventId: number): void {
    const eventBasic = this.historyPage?.getEventById(eventId) ?? null;
    this.detailPage = new EventDetailPage(eventId, eventBasic, () => {
      const detailEl = this.detailPageEl();
      this.removeFromContainer(detailEl);
      this.detailPage?.destroy();
      this.detailPage = null;
      this.currentPage = 'history';
      this.render();
    });
    this.currentPage = 'detail';
    this.render();
  }
}

// Bootstrap
const app = new App();
app.init();
